import type { ReactNode } from "react";
import type { Appearance, PaletteLike } from "@picnic-field/renderer/appearance";
import { useT } from "../i18n";
import { STATIC_TEXT, staticReason } from "../lib/staticReason";
import { ModelPreview } from "./ModelPreview";
import { TagChips } from "./ui/TagChips";

/**
 * 작품 카드(내 기계) — 제목(자동 작품명) · 모형 썸네일(Campus 색) · 원리 태그 칩 · 정적 전시 배지
 * (+ 배지 아래 한 줄: 움직이지 않는 이유와 달리려면 할 일 — 이유를 모르면 일반 문구).
 * FS-01-02 '내 기계' 카드와 FS-02 올리기 완료에 쓴다. 점수·순위·반응 수 없음(DR-01).
 */
export interface WorkCardData {
  title: string;
  tags: readonly string[];
  static: boolean;
  /** 보드(스냅샷 board — null = 프로그램 없이 올림). 모르면 생략 → 정적 안내는 일반 문구(프로그램 없음) */
  board?: string | null;
  appearance: Appearance | null;
  glbUrl: string | null;
}

export function WorkCard({
  work,
  palette,
  headingLevel = 2,
  heading,
  live = false,
  children,
}: {
  work: WorkCardData;
  palette: PaletteLike | null;
  headingLevel?: 2 | 3;
  /** 카드 제목 위 머리말(예: '내 기계') */
  heading?: string;
  /** true 면 썸네일을 돌려 볼 수 있다(기본: 정지 썸네일) */
  live?: boolean;
  children?: ReactNode;
}) {
  const t = useT();
  const H = headingLevel === 2 ? "h2" : "h3";
  const why = work.static ? staticReason(work.board) : null;
  return (
    <article className="pf-work" data-testid="work-card" aria-label={work.title}>
      <ModelPreview
        className="pf-work__thumb"
        glbUrl={work.glbUrl}
        appearance={work.appearance}
        palette={palette}
        label={t("student.work.thumbLabel", { title: work.title })}
        interactive={live}
        autoRotate={live}
        quality="low"
      />
      <div className="pf-work__body">
        {heading && <H className="pf-work__heading">{heading}</H>}
        {/* 와이어: 머리말(내 기계) 아래 작품명은 보조 글씨, 머리말이 없으면 작품명이 제목 */}
        <p className={heading ? "pf-work__subtitle" : "pf-work__title"} data-testid="work-title">
          {work.title}
        </p>
        <div className="pf-work__chips">
          <TagChips tags={work.tags} label={t("student.work.tags")} />
          <ul className="pf-chips" aria-label={t("student.work.state")}>
            <li className="pf-chip-static pf-chip-static--ok">{t("student.work.uploaded")}</li>
            {work.static && (
              <li className="pf-chip-static pf-chip-static--run" data-testid="static-badge">
                {t("student.work.static")}
              </li>
            )}
          </ul>
        </div>
        {why && (
          <p className="pf-work__subtitle" data-testid="static-hint" data-reason={why}>
            {t(STATIC_TEXT[why].short)}
          </p>
        )}
        {children}
      </div>
    </article>
  );
}
